import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline"
import LoadingButton from "@mui/lab/LoadingButton"
import Alert from "@mui/material/Alert"
import Autocomplete from "@mui/material/Autocomplete"
import Box from "@mui/material/Box"
import Button from "@mui/material/Button"
import CircularProgress from "@mui/material/CircularProgress"
import CssBaseline from "@mui/material/CssBaseline"
import Grid from "@mui/material/Grid"
import Paper from "@mui/material/Paper"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useMemo, useState } from "react"
import { useParams } from "react-router-dom"

import Column from "../../common/components/Column"
import Row from "../../common/components/Row"
import BookingQuestionFields from "../admin/meetings/BookingQuestionFields"
import {
  buildPublicBookingPayload,
  emptyBookingForm,
  formatMeetingDateTime,
  formatSlotTime,
  getApiErrorMessage,
  getTimeZoneOptions,
  questionErrorKey,
  validateBookingStep,
  validatePublicBooker,
  withAnswer,
} from "../admin/meetings/meetingUtils"
import { BOOKING_API, isLinkProblem } from "./bookingApi"
import { createBookingLinkClient } from "./bookingClient"

const STEP_DATE_TIME = 0
const STEP_DETAILS = 1
const STEP_DONE = 2

function Shell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "background.default",
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "center",
        px: 2,
        py: { xs: 3, sm: 6 },
      }}
    >
      <CssBaseline />
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2, sm: 4 }, maxWidth: 720, width: "100%" }}
      >
        <Column sx={{ gap: 2.5 }}>{children}</Column>
      </Paper>
    </Box>
  )
}

function emptyPublicForm() {
  return {
    ...emptyBookingForm(),
    bookerName: "",
    bookerEmail: "",
    website: "",
  }
}

// Book a meeting for ONE user, chosen by the link the page was opened with.
export default function PublicBookingPage() {
  const { token } = useParams()
  const client = useMemo(() => createBookingLinkClient(token), [token])

  const [info, setInfo] = useState(null)
  const [linkError, setLinkError] = useState("")
  const [step, setStep] = useState(STEP_DATE_TIME)
  const [form, setForm] = useState(emptyPublicForm)
  const [errors, setErrors] = useState({})
  const [availability, setAvailability] = useState(null)
  const [isLoadingSlots, setIsLoadingSlots] = useState(false)
  const [slotsError, setSlotsError] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState("")
  const [result, setResult] = useState(null)

  const { date, timeZone } = form
  // The time zones staff offer; every zone only if none are set up.
  const infoZones = info?.time_zones
  const timeZoneOptions = useMemo(
    () =>
      infoZones?.length
        ? infoZones.map((zone) => zone.time_zone)
        : getTimeZoneOptions(),
    [infoZones]
  )
  const timeZoneLabels = useMemo(
    () =>
      Object.fromEntries((infoZones || []).map((z) => [z.time_zone, z.label])),
    [infoZones]
  )
  // The same questions as the admin booking form, set up by staff.
  const questions = info?.questions || []

  useEffect(() => {
    let cancelled = false
    client
      .get(BOOKING_API.info)
      .then(({ data }) => {
        if (cancelled) return
        setInfo(data)
        // Start from the first offered zone when the browser's own is not one.
        const zones = data?.time_zones || []
        setForm((current) =>
          zones.length === 0 ||
          zones.some((zone) => zone.time_zone === current.timeZone)
            ? current
            : { ...current, timeZone: zones[0].time_zone }
        )
      })
      .catch((error) => {
        if (!cancelled)
          setLinkError(
            isLinkProblem(error) || error?.response?.status === 409
              ? getApiErrorMessage(error)
              : "This booking page could not be loaded. Please try again."
          )
      })
    return () => {
      cancelled = true
    }
  }, [client])

  useEffect(() => {
    if (!info || step !== STEP_DATE_TIME || !date) return undefined
    let cancelled = false
    setIsLoadingSlots(true)
    setSlotsError("")
    setAvailability(null)
    setForm((current) => ({ ...current, slot: null }))
    client
      .get(BOOKING_API.availability, { params: { date, timezone: timeZone } })
      .then(({ data }) => {
        if (!cancelled) setAvailability(data)
      })
      .catch((error) => {
        if (!cancelled)
          setSlotsError(
            getApiErrorMessage(error, "Could not load the available times.")
          )
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSlots(false)
      })
    return () => {
      cancelled = true
    }
  }, [client, info, step, date, timeZone])

  function updateForm(changes) {
    setForm((current) => ({ ...current, ...changes }))
    setErrors((current) => {
      const next = { ...current }
      Object.keys(changes).forEach((key) => delete next[key])
      return next
    })
  }

  function handleAnswerChange(question, value) {
    setForm((current) => withAnswer(current, question, value))
    setErrors((current) => {
      const next = { ...current }
      delete next[questionErrorKey(question)]
      return next
    })
  }

  function handleNext() {
    // Step 1 of the shared booking validation is the date and time check.
    const dateErrors = validateBookingStep(1, form)
    setErrors(dateErrors)
    if (Object.keys(dateErrors).length === 0) setStep(STEP_DETAILS)
  }

  function handleSubmit() {
    const detailErrors = {
      ...validateBookingStep(2, form, questions),
      ...validatePublicBooker(form),
    }
    setErrors(detailErrors)
    if (Object.keys(detailErrors).length > 0) return
    setIsSubmitting(true)
    setSubmitError("")
    client
      .post(BOOKING_API.create, buildPublicBookingPayload(form, questions))
      .then(({ data }) => {
        setResult(data)
        setStep(STEP_DONE)
      })
      .catch((error) => {
        if (isLinkProblem(error)) {
          setLinkError(getApiErrorMessage(error))
          return
        }
        setSubmitError(
          getApiErrorMessage(error, "The meeting could not be booked.")
        )
        if (error?.response?.data?.code === "slot_unavailable") {
          // Someone took the time meanwhile: back to the times, freshly loaded.
          setStep(STEP_DATE_TIME)
        }
      })
      .finally(() => setIsSubmitting(false))
  }

  function bookAnother() {
    setForm({ ...emptyPublicForm(), timeZone: form.timeZone })
    setErrors({})
    setSubmitError("")
    setResult(null)
    setAvailability(null)
    setStep(STEP_DATE_TIME)
  }

  function textField(field, label, extra = {}) {
    return (
      <TextField
        label={label}
        size="small"
        fullWidth
        value={form[field]}
        onChange={(event) => updateForm({ [field]: event.target.value })}
        error={Boolean(errors[field])}
        helperText={errors[field]}
        {...extra}
      />
    )
  }

  if (linkError) {
    return (
      <Shell>
        <Typography variant="h5" component="h1">
          Booking unavailable
        </Typography>
        <Alert severity="warning">{linkError}</Alert>
      </Shell>
    )
  }

  if (!info) {
    return (
      <Shell>
        <Row sx={{ justifyContent: "center", py: 4 }}>
          <CircularProgress aria-label="Loading booking page" />
        </Row>
      </Shell>
    )
  }

  if (step === STEP_DONE) {
    return (
      <Shell>
        <Row sx={{ gap: 1, alignItems: "center" }}>
          <CheckCircleOutlineIcon color="success" />
          <Typography variant="h5" component="h1">
            Meeting booked
          </Typography>
        </Row>
        <Typography variant="body1">
          {result?.candidate_name} is booked with {result?.customer_name} on{" "}
          <strong>
            {formatMeetingDateTime(result?.start_datetime, form.timeZone)}
          </strong>{" "}
          ({form.timeZone}). A calendar invitation was sent to them.
        </Typography>
        <Box>
          <Button variant="outlined" onClick={bookAnother}>
            Book another meeting
          </Button>
        </Box>
      </Shell>
    )
  }

  return (
    <Shell>
      <Column gap={0.5}>
        <Typography variant="h5" component="h1">
          Book a meeting with {info.customer_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          You are booking on behalf of {info.customer_name}. Only their calendar
          can be booked from this page.
        </Typography>
      </Column>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2">{info.title}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mt: 1, whiteSpace: "pre-line" }}
        >
          {info.description}
        </Typography>
      </Paper>

      {step === STEP_DATE_TIME ? (
        <Column gap={2}>
          <Typography variant="subtitle1">1. Pick a date and time</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={5}>
              <TextField
                label="Date"
                type="date"
                size="small"
                fullWidth
                value={form.date}
                onChange={(event) => updateForm({ date: event.target.value })}
                InputLabelProps={{ shrink: true }}
                error={Boolean(errors.date)}
                helperText={errors.date}
              />
            </Grid>
            <Grid item xs={12} sm={7}>
              <Autocomplete
                disableClearable
                size="small"
                options={timeZoneOptions}
                getOptionLabel={(zone) =>
                  timeZoneLabels[zone]
                    ? `${timeZoneLabels[zone]} (${zone})`
                    : zone
                }
                value={form.timeZone}
                onChange={(_, value) => updateForm({ timeZone: value })}
                renderInput={(params) => (
                  <TextField {...params} label="Time zone" />
                )}
              />
            </Grid>
          </Grid>
          {isLoadingSlots ? (
            <Row sx={{ justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} aria-label="Loading times" />
            </Row>
          ) : null}
          {slotsError ? <Alert severity="error">{slotsError}</Alert> : null}
          {submitError ? <Alert severity="warning">{submitError}</Alert> : null}
          {availability ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Available times, {availability.timezone}
              </Typography>
              <Grid container spacing={1}>
                {availability.slots.map((slot) => (
                  <Grid item xs={4} sm={3} key={slot.start}>
                    <Button
                      fullWidth
                      size="small"
                      variant={
                        form.slot?.start === slot.start
                          ? "contained"
                          : "outlined"
                      }
                      disabled={!slot.available}
                      onClick={() => updateForm({ slot })}
                    >
                      {formatSlotTime(slot, availability.timezone)}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : null}
          {errors.slot ? (
            <Typography variant="caption" color="error">
              {errors.slot}
            </Typography>
          ) : null}
          <Box>
            <Button variant="contained" onClick={handleNext}>
              Next
            </Button>
          </Box>
        </Column>
      ) : (
        <Column gap={2}>
          <Typography variant="subtitle1">2. Details</Typography>
          <Alert severity="info" icon={false}>
            {formatMeetingDateTime(form.slot?.start, form.timeZone)} (
            {form.timeZone}){" "}
            <Button size="small" onClick={() => setStep(STEP_DATE_TIME)}>
              Change time
            </Button>
          </Alert>
          <Typography variant="subtitle2">About you</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {textField("bookerName", "Your name *")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField("bookerEmail", "Your email *", { type: "email" })}
            </Grid>
          </Grid>
          <Typography variant="subtitle2">Who the meeting is with</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              {textField("firstName", "First name *")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField("lastName", "Last name *")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField("phone", "Phone *")}
            </Grid>
            <Grid item xs={12} sm={6}>
              {textField("email", "Email *", { type: "email" })}
            </Grid>
          </Grid>
          <Typography variant="subtitle2">Agent booking form</Typography>
          <BookingQuestionFields
            questions={questions}
            form={form}
            errors={errors}
            onAnswerChange={handleAnswerChange}
          />
          {/* Hidden from people; automated form fillers tend to complete it. */}
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            value={form.website}
            onChange={(event) => updateForm({ website: event.target.value })}
            style={{ position: "absolute", left: "-9999px", opacity: 0 }}
          />
          {submitError ? <Alert severity="error">{submitError}</Alert> : null}
          <Row gap={1}>
            <Button
              onClick={() => setStep(STEP_DATE_TIME)}
              disabled={isSubmitting}
            >
              Back
            </Button>
            <LoadingButton
              variant="contained"
              loading={isSubmitting}
              onClick={handleSubmit}
            >
              Confirm appointment
            </LoadingButton>
          </Row>
        </Column>
      )}
    </Shell>
  )
}
